import {useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {
    Button,
    Card,
    Cell,
    Header,
    PanelHeader,
    PanelHeaderBack,
    Spacing,
    Spinner,
    Tabs,
    TabsItem
} from '@vkontakte/vkui';
import React, {useEffect, useState} from 'react';
import {getWeeklyFriendsRating, getWeeklyPlayerRating} from "../../services/player";
import vkBridge from '@vkontakte/vk-bridge';
import {catalogPreviewPlayer, catalogPreviewRating, isCatalogPreview} from "../../utils/catalogPreview";

// Общие стили для карточек рейтинга
const ratingCardClass = "app-list-card arcade-card w-full text-gray-800";

// Отображает локальный или серверный портрет без внутреннего кеша компонента VKUI.
const PlayerAvatar = ({src, name}) => (
    <img src={src} alt={name || ""} className="w-12 h-12 rounded-full object-cover"/>
);

function Rating() {
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();

    const handleBackButton = () => {
        return isFirstPage ? routeNavigator.push('/') : routeNavigator.back();
    }

    return (
        <div className="app-screen">
            <PanelHeader
                before={<PanelHeaderBack className={"[&>svg]:p-0"} onClick={handleBackButton}/>}
                separator={null}
                className={"text-gray-800"}
            >
                Рейтинг
            </PanelHeader>

            <div className={"app-screen__content items-center text-gray-800"}>
                {/* Общий ограниченный контентный блок повторяет ширину max-w-lg главной страницы */}
                <Spacing size={12}/>
                <div className={"w-full"}>
                    <PlayerList/>
                </div>
                <Spacing size={32}/>
            </div>
        </div>
    )
}

function PlayerList() {
    const catalogPreview = isCatalogPreview();
    // Состояние для общего рейтинга
    const [players, setPlayers] = useState(catalogPreview ? catalogPreviewRating : []);
    const [currentPlayer, setCurrentPlayer] = useState(catalogPreview ? catalogPreviewPlayer : {});
    const [currentPlayerRank, setCurrentPlayerRank] = useState(catalogPreview ? 5 : 0);
    const [isLoading, setIsLoading] = useState(!catalogPreview);

    // Состояние для рейтинга друзей
    const [friends, setFriends] = useState([]);
    const [friendsLoading, setFriendsLoading] = useState(false);
    const [friendsPermissionDenied, setFriendsPermissionDenied] = useState(false);

    // Состояние для вкладок (табов)
    const [activeTab, setActiveTab] = useState('overall');

    // Загрузка общего рейтинга при монтировании компонента
    useEffect(() => {
        // Режим каталога использует локальный рейтинг для предсказуемого скриншота.
        if (catalogPreview) {
            return;
        }
        getWeeklyPlayerRating().then(resp => {
            const data = resp.data.response;
            setCurrentPlayer(data.player);
            setCurrentPlayerRank(data.player_rank);
            setPlayers(data.winners || []);
        }).catch(err => {
            console.log("getTopWinners error:", err);
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    // Загрузка рейтинга друзей при переключении на вкладку
    useEffect(() => {
        if (catalogPreview) {
            return;
        }
        if (activeTab === 'friends' && friends.length === 0) {
            fetchFriendsRating();
        }
    }, [activeTab]);

    // Определяем отказ в доступе к списку друзей по данным ошибки VK Bridge.
    const isFriendsPermissionDenied = (err) => {
        const reason = err?.error_data?.error_reason || err?.error_reason || '';
        if (typeof reason === 'string' && reason.toLowerCase().includes('denied')) {
            return true;
        }
        const code = err?.error_data?.error_code || err?.error_code;
        return code === 7 || code === 15;
    };

    const fetchFriendsRating = () => {
        setFriendsLoading(true);
        setFriendsPermissionDenied(false);
        vkBridge.send('VKWebAppGetAuthToken', {
            app_id: parseInt(process.env.REACT_APP_VK_APP_ID),
            scope: 'friends'
        }).then(tokenData => {
            return vkBridge.send('VKWebAppCallAPIMethod', {
                method: 'friends.get',
                params: {
                    v: '5.131',
                    fields: 'photo_100',
                    access_token: tokenData.access_token
                }
            });
        }).then(friendsData => {
            if (friendsData.response && friendsData.response.items && friendsData.response.items.length > 0) {
                const friendIDs = friendsData.response.items.map(friend => friend.id);
                return getWeeklyFriendsRating(friendIDs).then(friendsRatingResp => {
                    setFriends(friendsRatingResp.data.response || []);
                });
            }
            setFriends([]);
            return Promise.resolve();
        }).catch(err => {
            console.error("Failed to fetch friends rating:", err);
            if (isFriendsPermissionDenied(err)) {
                setFriendsPermissionDenied(true);
            }
        }).finally(() => {
            setFriendsLoading(false);
        });
    };

    const handleInvite = () => {
        vkBridge.send('VKWebAppShare', {
            text: "А сколько слов вы сможете придумать за минуту? Заходите в игру!",
        }).catch(e => console.log(e));
    };

    const renderOverallRating = () => (
        <>
            {isLoading ? <Spinner size={"large"}/> : (
                <>
                    {players.length > 0 ? (
                        <Card className={ratingCardClass}>
                            <Spacing size={12}/>
                            <p className={"text-xs text-gray-700 px-4"}>Недельный зачёт</p>
                            <Spacing size={12}/>
                            {players.map(player => (
                                <Cell before={<PlayerAvatar src={player.avatar} name={player.name}/>}
                                      key={player.id}
                                      subtitle={`Очков: ${player.weekly_score || 0} | Игр: ${player.weekly_games || 0}`}
                                      disabled={true}
                                      className={"vkui-cell-text"}
                                >
                                    <span className={"text-gray-800"}>{player.name}</span>
                                </Cell>
                            ))}
                            <Spacing size={12}/>
                        </Card>
                    ) : (
                        <Card className={ratingCardClass}>
                            <Header className={"text-gray-800"}>Рейтинг пока пуст</Header>
                        </Card>
                    )}

                    <Spacing size={24}/>

                    <Card className={ratingCardClass}>
                        <Header subtitle={`Позиция в рейтинге: ${currentPlayerRank}`}>
                            <span className={"text-gray-800"}>Ваш рейтинг</span>
                        </Header>
                        <Cell before={<PlayerAvatar src={currentPlayer.avatar} name={currentPlayer.name}/>}
                              key={currentPlayer.id}
                              subtitle={`Очков за неделю: ${currentPlayer.weekly_score || 0} | Игр: ${currentPlayer.weekly_games || 0}`}
                              disabled={true}
                              className={"vkui-cell-text"}
                        >
                            <span className={"text-gray-800"}>{currentPlayer.name}</span>
                        </Cell>
                        <Spacing size={12}/>
                    </Card>
                </>
            )}
        </>
    );

    const renderFriendsRating = () => (
        <Card className={ratingCardClass}>
            {friendsLoading ? (
                <div className={"p-4 flex justify-center items-center"}>
                    <Spinner size="large"/>
                </div>
            ) : (
                <>
                    {(friends && friends.length > 0) ? (
                        <>
                            <Spacing size={12}/>
                            {friends.map(player => (
                                <Cell before={<PlayerAvatar src={player.avatar} name={player.name}/>}
                                      key={player.id}
                                      subtitle={`Очков: ${player.weekly_score || 0} | Игр: ${player.weekly_games || 0}`}
                                      disabled={true}
                                      className={"vkui-cell-text"}
                                >
                                    <span className={"text-gray-800"}>{player.name}</span>
                                </Cell>
                            ))}
                            <Spacing size={12}/>
                        </>
                    ) : friendsPermissionDenied ? (
                        <div className={"p-4 flex flex-col items-center gap-y-3"}>
                            <p className={"text-sm text-gray-700 mb-4"}>
                                Разрешите доступ к списку друзей ВКонтакте, чтобы увидеть их рейтинг.
                            </p>
                            <Button
                                size="s"
                                stretched
                                mode="primary"
                                className={"app-btn-primary"}
                                onClick={fetchFriendsRating}>
                                Разрешить доступ
                            </Button>
                        </div>
                    ) : (
                        <div className={"p-4 flex flex-col items-center gap-y-3"}>
                            <p className={"text-sm text-gray-700 mb-4"}>
                                Никто из ваших друзей еще не играет. Отправьте приглашение, чтобы играть вместе!
                            </p>
                            <Button
                                size="s"
                                stretched
                                mode="primary"
                                className={"app-btn-primary"}
                                onClick={handleInvite}>
                                Отправить приглашение
                            </Button>
                        </div>
                    )}
                </>
            )}
        </Card>
    );

    const ratingContent = activeTab === 'overall' ? renderOverallRating() : renderFriendsRating();

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'friends' && friends.length === 0) {
            // Ставим загрузку сразу, чтобы кнопка не успевала мигнуть до спиннера.
            setFriendsLoading(true);
        }
    };

    return (
        <div className={"flex flex-col gap-4 w-full"}>
            <div className={"w-full"}>
                <Card className={ratingCardClass}>
                    <Tabs>
                        <TabsItem
                            selected={activeTab === 'overall'}
                            onClick={() => handleTabChange('overall')}
                        >
                            <span className={"text-gray-800"}>Общий</span>
                        </TabsItem>
                        <TabsItem
                            selected={activeTab === 'friends'}
                            onClick={() => handleTabChange('friends')}
                        >
                            <span className={"text-gray-800"}>Друзья</span>
                        </TabsItem>
                    </Tabs>
                </Card>
            </div>

            <div className={"flex flex-col gap-4 w-full"}>
                <div className={"w-full"}>
                    {ratingContent}
                </div>
            </div>
        </div>
    );
}

export default Rating;
